import { all, put, retry, select, takeEvery } from 'redux-saga/effects';
import ActionTypes from '../../../../Data/ActionTypes';
import {
  getMaxMatchesToDisplay,
  isMatchImpliedByPrivacyStatement,
  isSupplierMatch,
  SUBCATEGORY_MAX_MATCHES_DEFAULT,
} from '../../../../Helpers/supplierMatches';
import { exposeSupplierMatchesRequestedEvent } from '../../../../Helpers/events';
import canSkipSupplierMatchingQuery from './canSkipSupplierMatchingQuery';
import { getUrl } from '../../../../Helpers/submission';
import { getCampaignIdTrumpedByUrl } from '../../../../Helpers/campaignId';

/**
 * This generator function first determines whether supplier matching is required. Supplier matching is required if there is a supplier match component on the form and the form is not on a premium channel or alternatively if the form is configured to show matches on the thank you page.
 * If matching is required it then it prepares the form in the following ways:
 * 1. If the form is on a premium channel it disables supplier match selection for the rest of the form.
 * 2. It exposes an event containing information necessary to track matching within the data layer.
 *
 * @generator
 * @function prepareSupplierMatchingWorker
 * @yields {object} - Redux action object dispatched to update supplier match components or disable supplier match selection.
 */
export function* prepareSupplierMatchingWorker() {
  const { components, supplierMatches, optionalFormConfig, formConfig } =
    yield select();

  let subcategoryMaxMatches = SUBCATEGORY_MAX_MATCHES_DEFAULT; // Default in case there is no value on the component and the customer match request errors
  let maxMatches = subcategoryMaxMatches;
  let hideOnSingleMatch = false;

  const campaignId =
    getCampaignIdTrumpedByUrl(
      formConfig.campaignId,
      getUrl(formConfig.pageUrl),
      optionalFormConfig.useCidFromURL
    ) ?? undefined;

  if (supplierMatches.subcategoryMaxMatches) {
    subcategoryMaxMatches = supplierMatches.subcategoryMaxMatches;
  }

  if (supplierMatches.customerMatches.length > 0) {
    return;
  }

  // Get all Supplier match components.
  const supplierMatchComponents =
    Object.values(components).filter(isSupplierMatch);

  let canSkipSupplierMatching = false;
  // If there is no campaignId, we will not skip supplier matching. Otherwise we query the gateway to determine if supplier matching can be skipped.
  // Only activate this for Americas B2B (trading hub ID 5)
  // Exclude Medical Software US and Postage Meters US
  if (
    campaignId &&
    formConfig?.tradingHubId === '5' && // Americas B2B
    formConfig?.subcategoryId !== '2d42078f-ec70-ffea-7989-5ee784d69995' // Medical Software US
  ) {
    try {
      canSkipSupplierMatching = yield retry(
        3,
        1000,
        canSkipSupplierMatchingQuery,
        {
          campaignId,
        }
      );
    } catch (error) {
      // If the request fails after 3 retries, default to skipping supplier match selection. These failures will be reported by the gateway.
      canSkipSupplierMatching = true;
    }
  }

  if (canSkipSupplierMatching) {
    yield put({
      type: 'SUPPLIER_MATCHES/setSupplierMatchSelectionEnabled',
      payload: {
        supplierMatchSelectionEnabled: false,
      },
    });

    // Add a condition to all supplier match components which will hide them and skip their pages
    yield all(
      supplierMatchComponents.map((supplierMatchComponent) =>
        put({
          type: 'COMPONENTS/setCondition',
          payload: {
            componentId: supplierMatchComponent.component.id,
            condition: 'supplierMatchSelectionEnabled',
            value: false,
          },
        })
      )
    );
    if (!supplierMatches.showCustomerMatchesOnThankYouPage) {
      return;
    }
  }

  if (
    supplierMatchComponents.length === 0 &&
    !supplierMatches.showCustomerMatchesOnThankYouPage
  ) {
    return;
  }

  const firstSupplierMatchComponent = supplierMatchComponents[0];

  if (firstSupplierMatchComponent) {
    maxMatches = firstSupplierMatchComponent.component.maxMatches;
    hideOnSingleMatch = firstSupplierMatchComponent.component.hideOnSingleMatch;
  }

  const maxMatchesToDisplay = getMaxMatchesToDisplay({
    subcategoryMaxMatches,
    componentMaxMatches: maxMatches,
  }).toString();

  // Expose an event containing information necessary to track matching within the data layer. This is followed by a second event later in the session which indicates whether live matches have been obtained.
  exposeSupplierMatchesRequestedEvent({
    eventName: 'subcategoryMatchesRequested',
    iFrameId: optionalFormConfig.iFrameId,
    isSuccess: supplierMatches.subcategoryMatches.length > 0, // We would expect subcategoryMatches to be populated from Capture. We allow the form to continue since matching will still be possible if there are live suppliers available. We still send an event to make the data layer aware a supplier match journey is taking place but indicate the failure in this parameter.
    maxMatches: maxMatchesToDisplay,
    supplierMatches: supplierMatches.subcategoryMatches,
    isMatchImpliedByPrivacyStatement: isMatchImpliedByPrivacyStatement(
      hideOnSingleMatch,
      maxMatchesToDisplay,
      supplierMatches.subcategoryMatches,
      supplierMatches.supplierMatchSelectionEnabled
    ),
  });
}

export default function* prepareSupplierMatchingWatcher() {
  yield takeEvery(
    ActionTypes.PREPARE_SUPPLIER_MATCHING,
    prepareSupplierMatchingWorker
  );
}
